import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  SchedulingExceptionCreateInput,
  SchedulingHoursDayInput,
  SchedulingQuotaBatchInput,
  SchedulingQuotaCreateInput,
} from "@core/types";
import * as api from "./api";

const workshopsKey = ["portal", "scheduling", "workshops"] as const;
const hoursKey = (workshopId: string) =>
  ["portal", "scheduling", "hours", workshopId] as const;
const quotasKey = (workshopId: string, day: number) =>
  ["portal", "scheduling", "quotas", workshopId, day] as const;
const exceptionsKey = (workshopId: string) =>
  ["portal", "scheduling", "exceptions", workshopId] as const;

// SPEC-207: sin paginación, el portal administra un taller a la vez.
export const useWorkshops = () =>
  useQuery({ queryKey: workshopsKey, queryFn: api.listWorkshops });

export const useWorkshopHours = (workshopId: string | undefined) =>
  useQuery({
    queryKey: hoursKey(workshopId ?? ""),
    queryFn: () => api.getWorkshopHours(workshopId as string),
    enabled: Boolean(workshopId),
  });

export const useWorkshopQuotas = (
  workshopId: string | undefined,
  day: number,
) =>
  useQuery({
    queryKey: quotasKey(workshopId ?? "", day),
    queryFn: () => api.getWorkshopQuotas(workshopId as string, day),
    enabled: Boolean(workshopId),
  });

export const useSchedulingExceptions = (workshopId: string | undefined) =>
  useQuery({
    queryKey: exceptionsKey(workshopId ?? ""),
    queryFn: () => api.listSchedulingExceptions(workshopId as string),
    enabled: Boolean(workshopId),
  });

export function useSchedulingMutations(
  workshopId: string | undefined,
  day: number,
) {
  const client = useQueryClient();
  const id = workshopId ?? "";
  const invalidateWorkshops = () =>
    client.invalidateQueries({ queryKey: workshopsKey });
  const invalidateHours = () =>
    client.invalidateQueries({ queryKey: hoursKey(id) });
  const invalidateQuotas = () =>
    client.invalidateQueries({ queryKey: quotasKey(id, day) });
  const invalidateExceptions = () =>
    client.invalidateQueries({ queryKey: exceptionsKey(id) });

  return {
    saveHours: useMutation({
      mutationFn: (hours: SchedulingHoursDayInput[]) =>
        api.replaceWorkshopHours(id, hours),
      onSuccess: invalidateHours,
    }),
    addQuotaSlot: useMutation({
      mutationFn: (input: SchedulingQuotaCreateInput) =>
        api.createWorkshopQuotaSlot(id, input),
      onSuccess: invalidateQuotas,
    }),
    saveQuotas: useMutation({
      mutationFn: (input: SchedulingQuotaBatchInput) =>
        api.updateWorkshopQuotas(id, input),
      onSuccess: invalidateQuotas,
    }),
    createException: useMutation({
      mutationFn: (input: SchedulingExceptionCreateInput) =>
        api.createSchedulingException(id, input),
      onSuccess: invalidateExceptions,
    }),
    reopenException: useMutation({
      mutationFn: (exceptionId: string) =>
        api.deleteSchedulingException(id, exceptionId),
      onSuccess: invalidateExceptions,
    }),
    sync: useMutation({
      mutationFn: () => api.syncWorkshopSchedule(id),
      onSuccess: invalidateWorkshops,
    }),
  };
}
